import React, { useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useUser } from '../../contexts/UserContext';
import { useForm } from '../../hooks/useForm';
import { useAuthLogin, useDevLogin } from '../../services/auth';
import PublicLayout from '../layouts/PublicLayout';
import Panel from '../common/Panel';

const Login = () => {
  const { user, token, verifyId, setVerifyId, signIn } = useUser();
  const { form, onChange } = useForm({ email: '' });
  const navigate = useNavigate();

  const isDevMode =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_SKIP_AUTH === 'true';

  // Deep links (e.g. billing email buttons) must survive the sign-in dance.
  // The sessionStorage stash written by PrivateRoute is the single carrier
  // (router state dies on detours and slips past sign-out suppression);
  // consumed on read.
  const resolvePostLoginTarget = () => {
    const stashed = window.sessionStorage.getItem('postLoginRedirect');
    if (stashed) window.sessionStorage.removeItem('postLoginRedirect');
    // Nothing stashed: each role's own home (Nic 2026-10-09).
    return stashed || '/start';
  };

  useEffect(() => {
    if (user && token) {
      navigate(resolvePostLoginTarget());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (verifyId) {
      navigate('/verify');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyId]);

  const callApi = useAuthLogin(
    (res) => {
      setVerifyId(res.userId);
    },
    {
      onError: (err) => toast.error(err.message),
    },
  );

  const devLoginApi = useDevLogin(
    (res) => {
      // Resolve BEFORE signIn: signIn clears sessionStorage (the stash).
      const target = resolvePostLoginTarget();
      signIn(res, () => {
        navigate(target);
      });
    },
    {
      onError: (err) => toast.error(err.message),
    },
  );

  const onSubmit = (e) => {
    e.preventDefault();
    callApi.mutate(form.email);
  };

  const onDevSubmit = (e) => {
    e.preventDefault();
    devLoginApi.mutate(form.email);
  };

  return (
    <PublicLayout>
      <Row>
        <Col xs={12} sm={{ span: 8, offset: 2 }} md={{ span: 6, offset: 3 }}>
          <form onSubmit={onSubmit}>
            <Panel className="pb-3 mb-3">
              <h3 className="text-center">Returning Users</h3>
              <p className="text-center">
                Already have a claim in process with us? Please enter your email
                below to continue.
              </p>
            </Panel>
            <Panel className="pb-3 mb-3">
              <Form.Group>
                <Form.Label>Your Email</Form.Label>
                <Form.Control
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="your@email.com"
                  required
                />
              </Form.Group>
            </Panel>
            <Panel className="pb-3 mb-3">
              <Row>
                <Col>
                  <Button type="submit" variant="primary">
                    <FontAwesomeIcon
                      icon={callApi.isLoading ? faSpinner : faLock}
                      spin={callApi.isLoading}
                    />{' '}
                    Sign In
                  </Button>
                </Col>
                {isDevMode && (
                  <Col>
                    <Button
                      type="button"
                      variant="warning"
                      size="sm"
                      className="w-100"
                      onClick={onDevSubmit}
                      disabled={devLoginApi.isLoading}
                    >
                      <FontAwesomeIcon
                        icon={devLoginApi.isLoading ? faSpinner : faLock}
                        spin={devLoginApi.isLoading}
                      />{' '}
                      DEV ONLY - Quick Login
                    </Button>
                  </Col>
                )}
                <Col className="text-end">
                  <Link to="/register">Want to get started?</Link>
                </Col>
              </Row>
            </Panel>
          </form>
        </Col>
      </Row>
    </PublicLayout>
  );
};

export default Login;
